import { useState, type FormEvent } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Pencil, Trash2, KeyRound, Copy } from "lucide-react";
import { apiGet, apiPost, apiPatch, apiDelete } from "@/lib/api/client";
import { translateApiError } from "@/lib/errors/error-map";
import { toast } from "@/components/ui/toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Table, THead, TBody, Tr, Th, Td } from "@/components/ui/table";
import { PasswordField } from "@/components/password-field";
import { isPasswordValid } from "@/lib/validation/password";
import type { UserRole } from "@/lib/auth/store";

interface UserRecord {
  id: string;
  email: string;
  fullName: string;
  role: UserRole;
  isActive: boolean;
  canEditPrice?: boolean;
  canManageOrders?: boolean;
}

const ROLES: UserRole[] = ["admin", "manager", "cashier", "warehouse", "sales", "technician", "viewer"];
const ROLE_LABEL: Record<UserRole, string> = {
  admin: "Quản trị",
  manager: "Quản lý",
  cashier: "Thu ngân",
  warehouse: "Kho",
  sales: "Bán hàng + Nhập kho",
  technician: "Kỹ thuật",
  viewer: "Chỉ xem",
};

interface UserDialogProps {
  user: UserRecord | null;
  onClose: () => void;
}

function UserDialog({ user, onClose }: UserDialogProps) {
  const isEdit = Boolean(user);
  const qc = useQueryClient();
  const [email, setEmail] = useState(user?.email ?? "");
  const [fullName, setFullName] = useState(user?.fullName ?? "");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [role, setRole] = useState<UserRole>(user?.role ?? "cashier");
  const [isActive, setIsActive] = useState(user?.isActive ?? true);
  const [canEditPrice, setCanEditPrice] = useState(user?.canEditPrice ?? false);
  const [canManageOrders, setCanManageOrders] = useState(user?.canManageOrders ?? false);
  const [error, setError] = useState("");

  // Admin luôn có quyền sửa giá — cờ bị vô hiệu và luôn bật.
  const priceLockedOn = role === "admin";

  const passwordTouched = password.length > 0;
  const passwordValid = isPasswordValid(password);
  const confirmMatch = password === confirmPassword;
  const passwordRequired = !isEdit;
  const passwordOk = passwordRequired
    ? passwordValid && confirmMatch
    : !passwordTouched || (passwordValid && confirmMatch);

  const mutation = useMutation({
    mutationFn: (payload: unknown) =>
      isEdit
        ? apiPatch(`/v1/users/${user!.id}`, payload)
        : apiPost("/v1/users", payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["users"] });
      onClose();
    },
    onError: (e) => setError(translateApiError(e)),
  });

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError("");
    const payload: Record<string, unknown> = {
      email,
      fullName,
      role,
      isActive,
      canEditPrice: priceLockedOn ? true : canEditPrice,
      canManageOrders: priceLockedOn ? true : canManageOrders,
    };
    if (password) payload.password = password;
    mutation.mutate(payload);
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <h2 className="text-base font-semibold">{isEdit ? "Sửa người dùng" : "Thêm người dùng"}</h2>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-3">
            <div className="space-y-1.5">
              <Label>Email *</Label>
              <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>Họ tên *</Label>
              <Input required value={fullName} onChange={(e) => setFullName(e.target.value)} />
            </div>
            <PasswordField
              id="user-password"
              label={isEdit ? "Mật khẩu mới (để trống nếu không đổi)" : "Mật khẩu *"}
              value={password}
              onChange={setPassword}
              required={!isEdit}
              autoComplete="new-password"
              showRules
            />
            {(passwordRequired || passwordTouched) && (
              <PasswordField
                id="user-password-confirm"
                label="Xác nhận mật khẩu *"
                value={confirmPassword}
                onChange={setConfirmPassword}
                required
                autoComplete="new-password"
                error={
                  confirmPassword && !confirmMatch
                    ? "Mật khẩu xác nhận không khớp"
                    : undefined
                }
              />
            )}
            <div className="space-y-1.5">
              <Label>Vai trò</Label>
              <select
                className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
                value={role}
                onChange={(e) => setRole(e.target.value as UserRole)}
              >
                {ROLES.map((r) => <option key={r} value={r}>{ROLE_LABEL[r]}</option>)}
              </select>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
              Tài khoản đang hoạt động
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={priceLockedOn || canEditPrice}
                disabled={priceLockedOn}
                onChange={(e) => setCanEditPrice(e.target.checked)}
              />
              Được sửa giá / chiết khấu khi bán hàng
              {priceLockedOn && <span className="text-xs text-muted-foreground">(Quản trị luôn có quyền)</span>}
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={priceLockedOn || canManageOrders}
                disabled={priceLockedOn}
                onChange={(e) => setCanManageOrders(e.target.checked)}
              />
              Được sửa / xoá / huỷ phiếu (đơn hàng, phiếu nhập...)
              {priceLockedOn && <span className="text-xs text-muted-foreground">(Quản trị luôn có quyền)</span>}
            </label>
            {error && <p className="text-sm text-destructive">{error}</p>}
            <div className="flex gap-2 pt-1">
              <Button type="submit" size="sm" disabled={mutation.isPending || !passwordOk}>
                {mutation.isPending ? "Đang lưu…" : isEdit ? "Cập nhật" : "Tạo"}
              </Button>
              <Button type="button" variant="outline" size="sm" onClick={onClose}>Huỷ</Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

export function UsersPage() {
  const qc = useQueryClient();
  const [showDialog, setShowDialog] = useState(false);
  const [editUser, setEditUser] = useState<UserRecord | null>(null);
  const [tempPassword, setTempPassword] = useState<{ email: string; password: string } | null>(null);

  const { data: users = [], isLoading } = useQuery({
    queryKey: ["users"],
    queryFn: () => apiGet<UserRecord[]>("/v1/users"),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiDelete(`/v1/users/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["users"] }),
  });

  const resetPWMutation = useMutation({
    mutationFn: (id: string) =>
      apiPost<{ temporaryPassword: string }>(`/v1/admin/users/${id}/reset-password`),
    onSuccess: (data, _id) => {
      const target = users.find((u) => u.id === _id);
      setTempPassword({ email: target?.email ?? "", password: data.temporaryPassword });
    },
    onError: (e) => toast.error(translateApiError(e)),
  });

  function openCreate() {
    setEditUser(null);
    setShowDialog(true);
  }

  function openEdit(u: UserRecord) {
    setEditUser(u);
    setShowDialog(true);
  }

  function handleDelete(u: UserRecord) {
    if (window.confirm(`Xoá người dùng "${u.email}"?`)) {
      deleteMutation.mutate(u.id);
    }
  }

  function handleResetPassword(u: UserRecord) {
    if (window.confirm(`Reset mật khẩu cho "${u.email}"?`)) {
      resetPWMutation.mutate(u.id);
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">Người dùng</h1>
        <Button size="sm" onClick={openCreate}>
          <Plus size={16} /> Thêm người dùng
        </Button>
      </div>

      {isLoading ? (
        <p className="text-muted-foreground">Đang tải…</p>
      ) : (
        <Table>
          <THead>
            <Tr>
              <Th>Email</Th>
              <Th>Họ tên</Th>
              <Th>Vai trò</Th>
              <Th>Sửa giá</Th>
              <Th>Quản lý phiếu</Th>
              <Th>Trạng thái</Th>
              <Th />
            </Tr>
          </THead>
          <TBody>
            {users.length === 0 && (
              <Tr><Td colSpan={7} className="text-center text-muted-foreground">Không có dữ liệu</Td></Tr>
            )}
            {users.map((u) => (
              <Tr key={u.id}>
                <Td>{u.email}</Td>
                <Td>{u.fullName}</Td>
                <Td>
                  <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
                    {ROLE_LABEL[u.role] ?? u.role}
                  </span>
                </Td>
                <Td>
                  {u.role === "admin" || u.canEditPrice ? (
                    <span className="text-green-600 text-sm">Có</span>
                  ) : (
                    <span className="text-muted-foreground text-sm">—</span>
                  )}
                </Td>
                <Td>
                  {u.role === "admin" || u.canManageOrders ? (
                    <span className="text-green-600 text-sm">Có</span>
                  ) : (
                    <span className="text-muted-foreground text-sm">—</span>
                  )}
                </Td>
                <Td>
                  <span className={u.isActive ? "text-green-600 text-sm" : "text-muted-foreground text-sm"}>
                    {u.isActive ? "Hoạt động" : "Khoá"}
                  </span>
                </Td>
                <Td className="text-right">
                  <div className="flex justify-end gap-1">
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(u)} title="Sửa">
                      <Pencil size={14} />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleResetPassword(u)} title="Reset mật khẩu">
                      <KeyRound size={14} />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => handleDelete(u)} title="Xoá">
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </Td>
              </Tr>
            ))}
          </TBody>
        </Table>
      )}

      {showDialog && (
        <UserDialog user={editUser} onClose={() => setShowDialog(false)} />
      )}

      {tempPassword && (
        <TempPasswordModal
          email={tempPassword.email}
          password={tempPassword.password}
          onClose={() => setTempPassword(null)}
        />
      )}
    </div>
  );
}

function TempPasswordModal({
  email,
  password,
  onClose,
}: {
  email: string;
  password: string;
  onClose: () => void;
}) {
  function handleCopy() {
    void navigator.clipboard.writeText(password);
    toast.success("Đã sao chép mật khẩu");
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <Card className="w-full max-w-sm">
        <CardHeader>
          <h2 className="text-base font-semibold">Mật khẩu tạm thời</h2>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm">
            Mật khẩu mới cho <strong>{email}</strong>:
          </p>
          <div className="flex items-center gap-2 rounded-md border bg-muted px-3 py-2">
            <code className="flex-1 text-sm font-mono select-all">{password}</code>
            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={handleCopy} title="Sao chép">
              <Copy size={14} />
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            Mật khẩu chỉ hiển thị một lần. Người dùng sẽ phải đổi mật khẩu khi đăng nhập lần sau.
          </p>
          <Button size="sm" onClick={onClose}>Đóng</Button>
        </CardContent>
      </Card>
    </div>
  );
}
